import classes from './index.module.less';
import TalecastLogo from '@/assets/edit/logo.png';
import Icon from '@/components/iconfont/Icon.jsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { globalController, projectController, shareController } from '@/api/index.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatToLocalTime } from '@/utils/dayjs.js';
import message from '@/utils/message.jsx';
import { useApi } from '@/hooks/useApi.jsx';
import Confirm from '@/components/confirm/Confirm.jsx';
import { deleteProject } from '@/api/upload.js';
import CustomPlayerBar from '@/pages/recording/CustomPlayerBar/index.jsx';
import { getToken } from '@/utils/cookie.js';
import { useWebSocket } from '@/hooks/useWebSocket.js';
import { MessageEventType } from '@/utils/socket.js';
import { useDrawer } from '@/hooks/DrawerHooks.js';
import { useTranslation } from 'react-i18next';

export default function Share() {
  const navigate = useNavigate();
  function returnHome() {
    navigate('/home');
  }
  function toEdit() {
    window.onClickGATrackEvent('view-edit');
    navigate('/edit');
  }

  const { t } = useTranslation();
  const [projectId, setProjectId] = useState();
  const [info, setInfo] = useState({
    gmtModified: undefined,
    id: undefined,
    name: '',
    duration: undefined,
    backgroundId: undefined,
    shareCode: undefined,
    viewNumber: undefined,
    videoUrl: undefined,
  });
  const [isViewMode, setIsViewMode] = useState(true);
  const [isShareMode, setIsShareMode] = useState(false);
  const [isLogin, setLogin] = useState(false);
  const location = useLocation();
  function getViewData(id) {
    shareController.getDetailById(id).then((res) => {
      const { code, data } = res;
      if (code === 0) {
        setInfo({
          gmtModified: data.gmtModified,
          id: data.id,
          name: data.name,
          duration: data.duration,
          backgroundId: data.backgroundId,
          shareCode: data.shareCode,
          viewNumber: data.viewNumber,
          videoUrl: data.videoUrl,
        });
        let streams = data.mediaStreams;
        if (data.videoUrl) {
          streams = [
            {
              type: 1,
              url: data.videoUrl,
              mirror: data.isMirrorOpen,
            },
          ];
        }
        handleStreams(streams);
      }
    });
  }
  useEffect(() => {
    let mode = 'view';
    if (location.pathname === '/share') {
      mode = 'share';
      setIsViewMode(false);
      setIsShareMode(true);
    }

    if (mode === 'view') {
      const projectItem = JSON.parse(sessionStorage.getItem('projectItem'));
      if (projectItem) {
        setProjectId(projectItem.id);
        getViewData(projectItem.id);
      }
    } else {
      setLogin(!!getToken());
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      if (code) {
        shareController.getDetailByCode(code).then((res) => {
          const { code, data } = res;
          if (code === 0) {
            setInfo({
              name: data.name,
              duration: data.duration,
              backgroundId: data.backgroundId,
              userName: data.username,
              viewNumber: data.viewNumber,
              videoUrl: data.videoUrl,
            });
            let streams = data.mediaStreams;
            if (data.videoUrl) {
              streams = [
                {
                  type: 1,
                  url: data.videoUrl,
                  mirror: data.isMirrorOpen,
                },
              ];
            }
            handleStreams(streams);
          }
        });
      }
    }
  }, []);
  useWebSocket(
    'RETRY_SUCCESS',
    useCallback(() => {
      pauseMedia();
      stopMedia();
      setWaiting(false);
      getViewData(projectId);
    }, [projectId]),
  );

  const [waiting, setWaiting] = useState(false);
  const [action, setAction] = useState(null);
  useWebSocket(MessageEventType.PRE_DOWNLOAD_COMPLETE, ({ projectId, downloadUrl, isSuccess }) => {
    if (projectId === info.id && isSuccess && downloadUrl) {
      if (downloadUrl !== info.videoUrl) {
        replaceMedia(downloadUrl, false);
        setInfo({
          ...info,
          videoUrl: downloadUrl,
        });
      }
      setWaiting(false);
      if (action === 'share') {
        message.success('Copied to clipboard', {
          duration: 2000,
        });
      } else if (action === 'download') {
        downloadComposedVideo(info.id);
      }
      setAction(null);
    }
  });

  function replaceMedia(url, mirror) {
    pauseMedia();
    stopMedia();
    const streams = [
      {
        type: 1,
        url: url,
        mirror,
      },
    ];
    handleStreams(streams);
  }

  const { setIsOpen } = useDrawer();
  function preDownload() {
    setWaiting(true);
    return shareController
      .preDownloadVideo(projectId)
      .then((res) => {
        if (res?.code !== 0) {
          setWaiting(false);
        } else if (res?.data.url) {
          const { url, isMirrorOpen } = res.data;
          if (url !== info.videoUrl) {
            setInfo({
              ...info,
              videoUrl: url,
            });
            replaceMedia(url, isMirrorOpen);
          }
          setWaiting(false);
          return true;
        }
      })
      .catch((e) => {
        setWaiting(false);
        const error = JSON.parse(e);
        if (error?.code === -104) {
          setIsOpen(true);
        }
      });
  }

  async function download() {
    window.onClickGATrackEvent('view-download');
    setAction('download');
    const hasUrl = await preDownload();
    if (hasUrl) {
      downloadComposedVideo(projectId);
    }
  }

  function downloadComposedVideo(id) {
    projectController
      .download({
        projectId: id,
      })
      .then((res) => {
        if (res.code === 0) {
          window.location.href = res.data;
        }
      });
  }

  async function copyLink() {
    window.onClickGATrackEvent('view-share');
    setAction('share');
    generateShareLink();
    const hasUrl = await preDownload();
    if (hasUrl) {
      message.success('Copied to clipboard', {
        duration: 2000,
      });
    }
  }

  async function generateShareLink() {
    const link = `${window.location.origin}/share?code=${info.shareCode}`;
    await navigator.clipboard.writeText(link);
  }

  const inputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  function editName() {
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(-1, -1);
      }
    });
  }
  const { loading, execute } = useApi(projectController.rename);
  async function submitName(e) {
    const res = await execute({
      projectId: projectId,
      body: {
        projectName: e.target.value,
      },
    });
    if (res.code === 0) {
      setInfo({
        ...info,
        name: e.target.value,
      });
      setIsEditing(false);
    }
  }

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  function deleteCurrentProject() {
    deleteProject(projectId)
      .then((res) => {
        if (res.code === 0) {
          returnHome();
        } else {
          throw new Error(res);
        }
      })
      .catch(() => {
        message.error(t('As_Error_Network'), {
          id: 'deleteProjectFail',
        });
      });
  }

  const [bgUrl, setBgUrl] = useState(null);
  const [streams, setStreams] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef(null);
  const mainVideoRef = useRef(null);
  const sideVideoRef = useRef(null);
  const [projectDuration, setProjectDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [isPaused, setPaused] = useState(true);

  useEffect(() => {
    if (info.backgroundId && !bgUrl) {
      const presetDate = sessionStorage.getItem('presetData');
      if (presetDate) {
        setBgUrl(JSON.parse(presetDate).backgrounds[info.backgroundId - 1].bgUrl);
      } else {
        globalController.preset().then((res) => {
          if (res?.code === 0 && res.data) {
            setBgUrl(res.data.backgrounds[info.backgroundId - 1].bgUrl);
          }
        });
      }
    }
  }, [info, bgUrl]);

  const [onlyCamera, setOnlyCamera] = useState(false);
  function handleStreams(streams) {
    const hasContentStream = streams.find((stream) => stream.type === 1);
    const hasCameraStream = streams.find((stream) => stream.type === 2);
    setOnlyCamera(!hasContentStream && hasCameraStream);
    const transStreams = streams.map((stream) => {
      const res = {
        type: stream.type,
        url: stream.url || stream.rawUrl,
        mirror: stream.mirror,
      };
      if (stream.type === 0) {
        res.ref = audioRef;
      } else if (stream.type === 1) {
        res.ref = mainVideoRef;
      } else if (stream.type === 2) {
        res.ref = hasContentStream ? sideVideoRef : mainVideoRef;
      }
      return res;
    });
    setStreams(transStreams);
  }

  useEffect(() => {
    if (streams.length > 0) {
      loadMedia();
    }
  }, [streams]);

  async function loadMedia() {
    setIsLoaded(false);
    const audioPlayer = audioRef.current;
    const mainVideoPlayer = mainVideoRef.current;
    const sideVideoPlayer = sideVideoRef.current;
    const loadArray = [];
    setTime(0);
    if (mainVideoPlayer) {
      loadArray.push(
        mainVideoPlayer.readyState > 1
          ? undefined
          : new Promise((resolve) => {
              mainVideoPlayer.onloadeddata = resolve;
            }),
      );
    }
    if (sideVideoPlayer) {
      loadArray.push(
        sideVideoPlayer.readyState > 1
          ? undefined
          : new Promise((resolve) => {
              sideVideoPlayer.onloadeddata = resolve;
            }),
      );
    }
    if (audioPlayer) {
      loadArray.push(
        audioPlayer.readyState > 1
          ? undefined
          : new Promise((resolve) => {
              audioPlayer.onloadeddata = resolve;
            }),
      );
    }
    await Promise.all(loadArray);
    const media = mainVideoPlayer || audioPlayer;
    media.onended = function () {
      setPaused(true);
      setTime(this.duration);
    };
    media.ontimeupdate = function () {
      setTime(this.currentTime);
    };
    media.ondurationchange = function () {
      setProjectDuration(this.duration === Infinity ? info.duration / 1000 : this.duration);
    };
    setProjectDuration(media.duration === Infinity ? info.duration / 1000 : media.duration);
    setPaused(true);
    setIsLoaded(true);
  }

  const playMedia = useCallback(() => {
    window.onClickGATrackEvent('view-play');
    const audioPlayer = audioRef.current;
    const mainVideoPlayer = mainVideoRef.current;
    const sideVideoPlayer = sideVideoRef.current;
    const media = mainVideoPlayer || audioPlayer;
    if (media.currentTime / projectDuration > 0.99) {
      changeTime(0);
    }
    mainVideoPlayer?.play();
    sideVideoPlayer?.play();
    audioPlayer?.play();
    setPaused(false);
  }, [projectDuration]);

  const pauseMedia = useCallback(() => {
    const audioPlayer = audioRef.current;
    const mainVideoPlayer = mainVideoRef.current;
    const sideVideoPlayer = sideVideoRef.current;
    mainVideoPlayer?.pause();
    sideVideoPlayer?.pause();
    audioPlayer?.pause();
    setPaused(true);
  }, []);

  const changeTime = useCallback((time) => {
    const audioPlayer = audioRef.current;
    const mainVideoPlayer = mainVideoRef.current;
    const sideVideoPlayer = sideVideoRef.current;
    if (mainVideoPlayer) {
      mainVideoPlayer.currentTime = time;
    }
    if (sideVideoPlayer) {
      sideVideoPlayer.currentTime = time;
    }
    if (audioPlayer) {
      audioPlayer.currentTime = time;
    }
    setPaused(true);
  }, []);

  function stopMedia() {
    const audioPlayer = audioRef.current;
    const mainVideoPlayer = mainVideoRef.current;
    const sideVideoPlayer = sideVideoRef.current;
    if (mainVideoPlayer) {
      mainVideoPlayer.load();
    }
    if (sideVideoPlayer) {
      sideVideoPlayer.load();
    }
    if (audioPlayer) {
      audioPlayer.load();
    }
  }
  useEffect(() => {
    return stopMedia;
  }, []);

  return (
    <div className={classes.pageShare}>
      <header className={classes.flexCenter}>
        <div className={`${classes.home} ${classes.flexCenter}`}>
          <img src={TalecastLogo} alt="Home" onClick={returnHome} />

          <span>{t('As_Preview_Header')}</span>

          {isShareMode && (
            <div className={`${classes.userName} ${classes.flexCenter}`}>
              {t('As_Preview_Shared')} {info.userName}
            </div>
          )}
        </div>

        {isViewMode ? (
          <div className={`${classes.actions} ${classes.flexCenter}`}>
            <span>
              {t('As_Preview_UpdateTime')}
              {info.gmtModified && formatToLocalTime(info.gmtModified)}
            </span>
            <button className="btn btn-outline btn-sm" onClick={toEdit}>
              <Icon name="icon-edit"></Icon>
              <span>{t('As_Preview_Edits')}</span>
            </button>
          </div>
        ) : !isLogin ? (
          <button className="btn btn-primary btn-sm" onClick={returnHome}>
            {t('As_Preview_Free')}
          </button>
        ) : null}
      </header>

      <main>
        <section>
          {isViewMode ? (
            <header className={classes.flexCenter}>
              <div className={`${classes.name} ${classes.flexCenter}`}>
                <Icon name="icon-edit"></Icon>
                <span
                  className={classes.projectName}
                  onClick={editName}
                  style={{ display: isEditing ? 'none' : 'inline-block' }}>
                  {info.name}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  defaultValue={info.name}
                  onBlur={submitName}
                  disabled={loading}
                  className="input input-bordered input-sm w-full max-w-xs"
                  style={{ display: isEditing ? 'inline' : 'none' }}
                />
                {loading && <span className="loading loading-spinner loading-sm ml-2"></span>}
              </div>

              <div className={`${classes.views} ${classes.flexCenter}`}>
                <Icon name="icon-view"></Icon>
                <span>
                  {info.viewNumber} {t('As_Preview_Views')}
                </span>
              </div>
            </header>
          ) : (
            <header className={classes.flexCenter}>
              <span className={classes.projectName}>{info.name}</span>
            </header>
          )}

          <div className={classes.videoContainer}>
            {streams.map((stream) => {
              if (stream.type === 0) {
                return <audio ref={stream.ref} key={stream.type} src={stream.url}></audio>;
              } else {
                return (
                  <video
                    ref={stream.ref}
                    key={stream.type}
                    src={stream.url}
                    className={`${stream.type === 2 && onlyCamera ? classes.onlyCamera : ''}`}
                    style={{ transform: `rotateY(${stream.mirror ? '180deg' : 0})` }}></video>
                );
              }
            })}

            {isLoaded && isPaused && (
              <div className={classes.playBtn} onClick={playMedia}>
                <Icon name="icon-play"></Icon>
              </div>
            )}

            <div
              style={{
                display: isLoaded ? 'block' : 'none',
                position: 'absolute',
                bottom: '20px',
                width: '100%',
                padding: '0 20px',
              }}>
              <CustomPlayerBar
                duration={projectDuration}
                time={time}
                isPaused={isPaused}
                handlePlay={playMedia}
                handlePause={pauseMedia}
                handleChangeTime={changeTime}></CustomPlayerBar>
            </div>
          </div>

          {isViewMode && (
            <footer className={classes.flexCenter}>
              <button className="btn btn-ghost" onClick={() => setShowDeleteDialog(true)}>
                {t('As_Preview_PreviewDelete')}
              </button>

              <Confirm
                show={showDeleteDialog}
                title={t('As_Preview_DeleteTitle')}
                content={t('As_Preview_DeleteDesc')}
                okText={t('As_Preview_DeleteConfirm')}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={deleteCurrentProject}></Confirm>

              <div className={classes.flexCenter}>
                <button
                  className="btn btn-outline btn-primary"
                  onClick={download}
                  disabled={waiting}>
                  {waiting ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    <Icon name="icon-download"></Icon>
                  )}
                  {t('As_Preview_Download')}
                </button>
                <button className="btn btn-primary ml-2.5" onClick={copyLink} disabled={waiting}>
                  {waiting ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    <Icon name="icon-share" style={{ fontSize: '20px' }}></Icon>
                  )}
                  {t('As_Preview_Share')}
                </button>
              </div>
            </footer>
          )}
        </section>
      </main>
    </div>
  );
}
